import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductAttribute } from '@/services/productService';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { TableRow, TableCell } from '@/components/ui/table';
import { Check, X } from 'lucide-react';
import { format } from 'date-fns';
import { useEffectiveAttributes } from '@/hooks/useEffectiveAttributes';
import { useAttributeTranslations } from '@/hooks/useAttributeTranslations';

interface ProductRowDetailsProps {
  product: {
    id: number;
    name: string;
    sku: string;
    description?: string;
    brand?: string;
    category_name?: string;
    family_name?: string;
    attributes?: any[]; // Legacy field, will be replaced by effective attributes
  };
  zebra?: boolean;
  locale?: string;
  channel?: string;
}

interface Attribute {
  id: string | number;
  name: string;
  value: any;
  group: string;
  locale?: string;
  channel?: string;
}

interface AttributeGroup {
  name: string;
  attributes: Attribute[];
}

// Format attribute value for display (enterprise-grade, handles objects, booleans, etc.)
function formatAttributeValue(
  value: any,
  translations: Record<string, string>,
  t?: (k: string, d?: string) => string
): React.ReactNode {
  if (value === null || value === undefined || value === '') {
    return (
      <span className="italic text-slate-400">
        {t
          ? t('productdetailpage.productComponents.productRowDetails.noValue', 'No value')
          : 'No value'}
      </span>
    );
  }
  if (typeof value === 'boolean') {
    return value ? (
      <Badge variant="outline" className="border-green-100 bg-green-50 text-green-600">
        <Check className="mr-1 size-3" />
        {translations['yes'] || 'Yes'}
      </Badge>
    ) : (
      <Badge variant="outline" className="border-red-100 bg-red-50 text-red-600">
        <X className="mr-1 size-3" />
        {translations['no'] || 'No'}
      </Badge>
    );
  }
  if (typeof value === 'object') {
    // Price: { amount, currency }
    if ('amount' in value && 'currency' in value) {
      return `${value.amount} ${value.currency}`;
    }
    // Measurement: { amount, unit }
    if ('amount' in value && 'unit' in value) {
      return `${value.amount} ${value.unit || ''}`;
    }
    // Media: { asset_id }
    if ('asset_id' in value) {
      return `Asset #${value.asset_id}`;
    }
    // Fallback: JSON stringify
    return JSON.stringify(value);
  }
  // Date (ISO string)
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    try {
      return format(new Date(value), 'yyyy-MM-dd');
    } catch {
      /* ignore */
    }
  }
  // URL/email/phone
  if (typeof value === 'string' && (value.startsWith('http') || value.includes('@'))) {
    return (
      <a
        href={value.startsWith('http') ? value : `mailto:${value}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary underline"
      >
        {value}
      </a>
    );
  }
  // Rich text (basic)
  if (typeof value === 'string' && /<[^>]+>/.test(value)) {
    return <span dangerouslySetInnerHTML={{ __html: value }} />;
  }
  // Default: string
  return String(value);
}

const ProductRowDetails: React.FC<ProductRowDetailsProps> = ({
  product,
  zebra,
  locale,
  channel,
}) => {
  // Get boolean translations
  const { getBooleanText } = useAttributeTranslations();
  const booleanTranslations = {
    yes: getBooleanText('yes'),
    no: getBooleanText('no'),
  };
  const { t } = useTranslation('translation');

  // Use effective attributes instead of product.attributes
  const {
    data: effectiveGroups,
    isLoading,
    error,
  } = useEffectiveAttributes(product.id, locale, channel, true);

  // Process and organize effective attributes into groups
  const attributeGroups = useMemo(() => {
    if (!effectiveGroups || effectiveGroups.length === 0) {
      return [];
    }

    // Transform effective attributes to the expected format
    return effectiveGroups
      .map(group => ({
        name: group.groupName,
        attributes: group.attributes
          .filter(
            attr =>
              attr.value !== undefined &&
              attr.value !== null &&
              (attr.value !== '' || typeof attr.value === 'boolean')
          )
          .map(attr => ({
            id: attr.id,
            name: attr.label,
            value: attr.value,
            group: group.groupName,
            locale: attr.locale,
            channel: attr.channel,
          })),
      }))
      .filter(group => group.attributes.length > 0); // Remove empty groups
  }, [effectiveGroups]);

  // Legacy fallback: Process product.attributes if effective attributes are not available
  const legacyAttributeGroups = useMemo(() => {
    if (
      !product.attributes ||
      !Array.isArray(product.attributes) ||
      product.attributes.length === 0
    ) {
      return [];
    }

    // Check if we have a nested group structure from the API
    // The API returns: [{ id: 1, name: "tech", items: [{...attributes}] }]
    if (
      product.attributes.length > 0 &&
      typeof product.attributes[0] === 'object' &&
      product.attributes[0] !== null &&
      'name' in product.attributes[0] &&
      'items' in product.attributes[0]
    ) {
      // We have groups directly from the API
      return product.attributes
        .map((group: any) => {
          return {
            name: group.name,
            attributes: (group.items || [])
              .filter((item: any) => item.value !== undefined)
              .map((item: any) => {
                // Extract attribute info from each item
                const attrName =
                  item.attribute_label ||
                  (typeof item.attribute === 'object'
                    ? item.attribute.label
                    : `Attribute ${item.attribute}`);

                return {
                  id: item.id || `group-${group.id}-attr-${item.attribute}`,
                  name: attrName,
                  value: item.value,
                  group: group.name,
                  locale: item.locale,
                  channel: item.channel,
                };
              })
              .filter((attr: Attribute) => attr.name),
          };
        })
        .filter(group => group.attributes.length > 0); // Remove empty groups
    }

    // Fallback: flat attributes array
    const groupedAttrs: Record<string, Attribute[]> = {};

    product.attributes.forEach((attr: any) => {
      if (
        attr.value === undefined ||
        attr.value === null ||
        (attr.value === '' && typeof attr.value !== 'boolean')
      )
        return; // Skip empty values but allow false booleans

      const groupName = attr.group || 'General';
      if (!groupedAttrs[groupName]) {
        groupedAttrs[groupName] = [];
      }

      groupedAttrs[groupName].push({
        id: attr.id || Math.random(),
        name: attr.name || attr.attribute_label || 'Unknown',
        value: attr.value,
        group: groupName,
        locale: attr.locale,
        channel: attr.channel,
      });
    });

    return Object.entries(groupedAttrs).map(([name, attributes]) => ({
      name,
      attributes,
    }));
  }, [product.attributes]);

  // Use effective attributes if available, otherwise fall back to legacy
  const finalAttributeGroups = attributeGroups.length > 0 ? attributeGroups : legacyAttributeGroups;

  // If no attributes, render a simple message
  if (finalAttributeGroups.length === 0) {
    return (
      <tr
        className={cn('border-b', zebra ? 'bg-slate-950/3' : 'bg-white')}
        aria-label={`Product details for ${product.name}`}
      >
        <TableCell colSpan={999} className="p-4 text-center text-sm text-muted-foreground">
          No additional product attributes available
        </TableCell>
      </tr>
    );
  }

  return (
    <tr
      className={cn('border-b', zebra ? 'bg-muted/30' : 'bg-background')}
      aria-label={`Product details for ${product.name}`}
    >
      <TableCell colSpan={999} className="p-2">
        <div className="overflow-hidden rounded-md border border-border bg-card shadow-sm">
          <div className="p-3">
            <h3 className="mb-2 text-sm font-medium text-foreground dark:text-white">
              {t(
                'productdetailpage.productComponents.productRowDetails.attributes.title',
                'Product Attributes'
              )}
            </h3>

            <div className="flex flex-wrap gap-2">
              {finalAttributeGroups.map(group => (
                <div
                  key={group.name}
                  className="min-w-[240px] max-w-[320px] shrink-0 rounded-md border border-border bg-card p-2 hover:bg-accent/60 dark:hover:bg-accent/40"
                >
                  {/* group name + locale / channel badges */}
                  {(() => {
                    const first = group.attributes[0] as any; // safe: group has ≥1 attr
                    const loc = first?.locale;
                    const ch = first?.channel;

                    return (
                      <h4 className="mb-1 flex items-center gap-1 border-t border-border pt-1 text-xs font-medium text-foreground">
                        {group.name}
                        {loc && (
                          <span className="inline-block rounded-full bg-muted px-1 py-0.5 text-[10px] font-normal text-muted-foreground">
                            {loc}
                          </span>
                        )}
                        {ch && (
                          <span className="inline-block rounded-full bg-muted px-1 py-0.5 text-[10px] font-normal text-muted-foreground">
                            {ch}
                          </span>
                        )}
                      </h4>
                    );
                  })()}

                  <dl className="space-y-1">
                    {group.attributes.map((attr: Attribute) => (
                      <div key={attr.id} className="grid grid-cols-2 text-xs">
                        <dt className="truncate font-medium text-muted-foreground dark:text-gray-300">
                          {attr.name}:
                        </dt>
                        <dd className="truncate pl-1 text-foreground dark:text-white">
                          {formatAttributeValue(attr.value, booleanTranslations, t)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}
            </div>
          </div>
        </div>
      </TableCell>
    </tr>
  );
};

export default ProductRowDetails;
